import { create } from 'zustand';

export type Player = {
  id: string;
  name: string;
  chips: number;
  seat: number;
  avatarUrl?: string;
  isActive: boolean;
  isEliminated: boolean;
};

export type SeatState = {
  seat: number;
  userId?: string;
  displayName?: string;
  avatarUrl?: string;
  chips?: number;
  connected?: boolean;
  active?: boolean;
  allIn?: boolean;
  currentContribution?: number;
  totalContribution?: number;
  handsPlayed?: number;
};

export type HandResult = {
  pots: Array<{
    pot: number;
    houseTake?: number;
    winners: Array<{ seat: number; userId: string; amount: number; hand?: string }>;
  }>;
  community: string[];
};

export type TableState = {
  tableId: string | null;
  players: Player[];
  seats: SeatState[];
  communityCards: string[];
  pot: number;
  currentBet: number;
  lastRaiseAmount: number;
  dealerSeat: number;
  currentPlayerSeat: number;
  actionTimeoutSeconds: number;
  remainingSeconds: number;
  mySeat: number;
  myHoleCards: string[];
  handResult: HandResult | null;
  notification: string | null;
  accountBalance: number | null;
  initialBuyIn: number | null;
  topUpUsed: number;
  phase: 'idle' | 'preflop' | 'flop' | 'turn' | 'river' | 'showdown';
  animationState: string;
  setTableId: (tableId: string | null) => void;
  joinTable: (players: Player[], dealerSeat: number) => void;
  updateState: (state: Partial<TableState>) => void;
  dealCards: (cards: string[]) => void;
  setPhase: (phase: TableState['phase']) => void;
  setCurrentPlayer: (seat: number) => void;
  setPot: (pot: number) => void;
  setCommunityCards: (cards: string[]) => void;
  setPrivateState: (seat: number, holeCards: string[]) => void;
  setActionRequest: (currentTurnIndex: number, currentBet: number, lastRaiseAmount: number, remainingSeconds: number, actionTimeoutSeconds: number) => void;
  setHandResult: (result: HandResult | null) => void;
  setNotification: (message: string | null) => void;
  setAccountBalance: (balance: number | null) => void;
  setInitialBuyIn: (amount: number | null) => void;
  addTopUpUsed: (amount: number) => void;
  resetTopUpUsed: () => void;
};

export const useTableStore = create<TableState>((set) => ({
  tableId: null,
  players: [],
  seats: [],
  communityCards: [],
  pot: 0,
  currentBet: 0,
  lastRaiseAmount: 0,
  dealerSeat: 0,
  currentPlayerSeat: 0,
  actionTimeoutSeconds: 0,
  remainingSeconds: 0,
  mySeat: -1,
  myHoleCards: [],
  handResult: null,
  notification: null,
  accountBalance: null,
  initialBuyIn: null,
  topUpUsed: 0,
  phase: 'idle',
  animationState: 'idle',
  setTableId: (tableId) => set({ tableId }),
  joinTable: (players, dealerSeat) => set({ players, dealerSeat }),
  updateState: (state) => set(state),
  dealCards: (cards) => set({ communityCards: cards }),
  setPhase: (phase) => set({ phase }),
  setCurrentPlayer: (seat) => set({ currentPlayerSeat: seat }),
  setPot: (pot) => set({ pot }),
  setCommunityCards: (cards) => set({ communityCards: cards }),
  setPrivateState: (seat, holeCards) => set({ mySeat: seat, myHoleCards: holeCards ?? [] }),
  setActionRequest: (currentTurnIndex, currentBet, lastRaiseAmount, remainingSeconds, actionTimeoutSeconds) =>
    set({ currentPlayerSeat: currentTurnIndex, currentBet, lastRaiseAmount, remainingSeconds, actionTimeoutSeconds }),
  setHandResult: (result) => set({ handResult: result }),
  setNotification: (message) => set({ notification: message }),
  setAccountBalance: (balance) => set({ accountBalance: balance }),
  setInitialBuyIn: (amount) => set({ initialBuyIn: amount, topUpUsed: 0 }),
  addTopUpUsed: (amount) => set((state) => ({ topUpUsed: state.topUpUsed + Math.max(0, amount) })),
  resetTopUpUsed: () => set({ topUpUsed: 0 }),
}));
